import React, { useEffect, useState } from "react";
import { PlayCircleFilled, YoutubeOutlined } from "@ant-design/icons";
import {
  buildYouTubeEmbedUrl,
  buildYouTubeThumbnailUrl,
  buildYouTubeWatchUrl,
  getYouTubeId,
  getYouTubeStart,
} from "../utils/youtube";

// ══════════════════════════════════════════════════════════════
// Reproductor de YouTube para Noticias (agregado 06/10/2026)
//
// Muestra primero la miniatura con un botón de play y recién al hacer
// clic carga el iframe de YouTube. Así:
// - el Dashboard no carga un reproductor por cada noticia;
// - en el carrusel no suenan dos videos a la vez (react-slick duplica
//   slides cuando infinite=true).
//
// stopSignal: cada vez que cambia este número el video se detiene y
// vuelve a la miniatura (el carrusel lo cambia al pasar de slide).
// ══════════════════════════════════════════════════════════════

interface YouTubeEmbedProps {
  url: string;
  title?: string;
  stopSignal?: number;
  onPlay?: () => void;
  /** XP ganable mostrada junto al link de YouTube (0/undefined = oculta). */
  exp?: number;
}

const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({
  url,
  title = "Video de YouTube",
  stopSignal,
  onPlay,
  exp,
}) => {
  const videoId = getYouTubeId(url);
  const start = getYouTubeStart(url);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    setPlaying(false);
  }, [stopSignal, videoId]);

  if (!videoId) return null;

  const handlePlay = () => {
    setPlaying(true);
    onPlay?.();
  };

  return (
    <div>
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 9",
          borderRadius: 16,
          overflow: "hidden",
          border: "1px solid var(--dashboard-panel-border)",
          background: "#000",
        }}
      >
        {playing ? (
          <iframe
            src={buildYouTubeEmbedUrl(videoId, start)}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            style={{
              width: "100%",
              height: "100%",
              border: 0,
              display: "block",
            }}
          />
        ) : (
          <button
            type="button"
            onClick={handlePlay}
            aria-label={`Reproducir: ${title}`}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              padding: 0,
              border: 0,
              cursor: "pointer",
              background: "transparent",
            }}
          >
            <img
              src={buildYouTubeThumbnailUrl(videoId)}
              alt=""
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            <span
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(47, 43, 116, 0.25)",
              }}
            >
              <PlayCircleFilled
                style={{
                  fontSize: 64,
                  color: "#fff",
                  filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.45))",
                }}
              />
            </span>
          </button>
        )}
      </div>

      {/* Algunos canales bloquean la reproducción fuera de YouTube:
          este link deja siempre una salida para verlo igual. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginTop: 6,
          flexWrap: "wrap",
        }}
      >
        <a
          href={buildYouTubeWatchUrl(videoId)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            fontWeight: 700,
            color: "#7c5cfc",
          }}
        >
          <YoutubeOutlined /> Ver en YouTube
        </a>

        {exp ? (
          <span
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: "#fff",
              padding: "3px 10px",
              borderRadius: 999,
              background: "linear-gradient(135deg, #7c5cfc 0%, #9b7dfc 100%)",
              whiteSpace: "nowrap",
            }}
          >
            PODÉS GANAR {exp} EXP
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default YouTubeEmbed;
